import React, { useState } from "react";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import ReportProblemGuide from "../../../components/support/ReportProblemGuide";
import PrimaryAddButton from "../../../components/buttons/PrimaryAddButton";
import NormalSelectInputField from "../../../components/inputs/NormalSelectInputField";
import { NormalTextAreaField } from "../../../components/inputs/NormalInputField";
import { toast } from "react-toastify";

export const GetSupportPage = () => {
  const [selectedProblem, setSelectedProblem] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const btnIsDisabled = !selectedProblem || !description || isLoading;

  const knownProblems = [
    { label: "Technical Issue", value: "technical" },
    { label: "Billing Problem", value: "billing" },
    { label: "Account Access", value: "access" },
    { label: "Performance Issues", value: "performance" },
    { label: "Data Discrepancy", value: "data" },
    { label: "Other", value: "other" },
  ];

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);

    if (totalSize > 5 * 1024 * 1024) {
      setError("Total file size must not exceed 5MB");
      e.target.value = null;
      return;
    }

    const invalidFiles = selectedFiles.filter(
      (file) => !file.type.startsWith("image/")
    );
    if (invalidFiles.length > 0) {
      setError("Only image files are allowed");
      e.target.value = null;
      return;
    }

    setError("");

    // Read file data as base64
    const filePromises = selectedFiles.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) =>
          resolve({
            file: file,
            data: e.target.result.split(",")[1], // base64 string
            name: file.name,
            size: file.size,
            type: file.type,
          });
        reader.onerror = reject;
        reader.readAsDataURL(file); // This gives you base64
      });
    });

    try {
      const filesWithData = await Promise.all(filePromises);
      console.log("filesWithData", filesWithData);
      setFiles(filesWithData);
    } catch (error) {
      setError("Error reading files");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!selectedProblem) {
      setError("Please select a problem type");
      setIsLoading(false);
      return;
    }

    if (!description.trim()) {
      setError("Please provide a description of the problem");
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        type: selectedProblem,
        description: description,
        attachments: files.map((file) => ({
          filename: file.name,
          content: file.data, // base64 string (without prefix)
          type: file.type,
          disposition: "attachment",
        })),
      };

      const token = getToken();
      await instance.post("/doctor/problem-reports/new", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Report sent. You will be contacted shortly.");

      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (err) {
      setError(
        err.response
          ? err.response.data.message
          : "Failed to submit the problem report. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#fafafa] max-w-[100%] w-full min-h-screen overflow-y-scroll px-7 py-5">
      <div className="text-2xl font-bold mb-5">Get Support</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <form
          onSubmit={handleSubmit}
          className="space-y-6 border p-5 rounded-md"
        >
          <div>
            <NormalSelectInputField
              title="Type of Problem"
              required={true}
              values={knownProblems}
              value={selectedProblem}
              onChange={(e) => setSelectedProblem(e.target.value)}
            />
          </div>

          <div>
            <NormalTextAreaField
              title="Problem Description"
              required={true}
              placeholder="Please describe your problem in detail..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={15}
            />
          </div>

          <div>
            <div className="font-semibold uppercase text-[12px] text-gray-600 mb-2">
              Attach Images <span className="text-gray-400">(Optional)</span>
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-primary file:text-white
                hover:file:bg-primary-dark
                file:cursor-pointer cursor-pointer"
            />
            <div className="mt-1 text-xs text-gray-500">
              Max total size: 5MB. Allowed formats: Images only (jpg, png, gif,
              etc)
            </div>
            {files.length > 0 && (
              <div className="mt-2 text-sm text-gray-600">
                Selected files: {files.length}{" "}
                {files.length === 1 ? "file" : "files"}
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm font-semibold text-red-500">{error}</div>
          )}

          <div>
            <PrimaryAddButton
              title="Submit Report"
              type="submit"
              isLoading={isLoading}
              disabled={btnIsDisabled}
              color="white"
            />
          </div>
        </form>

        <ReportProblemGuide problemType={selectedProblem} />
      </div>
    </div>
  );
};
